import Axios                  from 'axios'
import { PaytableSection }    from '../paytableSection.class'
import { BUTTON_CONFIG }      from '../../../../constants/button/button-config.constant'
import { ORIENTATION }        from '../../../../constants/orientation/orientation.constant'
import { MENU_BUTTON_CONFIG } from '../../../../constants/button/menu-button-config.constant'
import { Action }             from '../../../action/action.class'
import { ICONS }              from '../../../../constants/icons/icons.constant'
import { IS_NATIVE } from '../../../../constants/is-native.constant'

/**
 * @file Defines the {@link GameGuideManager} class.
 * @author Joe Thompson <joe.thompson@1x2network.com>
 * @version 1.0.32
 */

 /** Class for handling game guide in paytable */
 export class GameGuideManager extends PaytableSection {

  /**
   * Reference to game guide content node
   * @memberof GameGuideManager
   * @protected
   * @instance
   * @type {HTMLElement}
   */
  _body

   /**
    * Game guide on click event handler
    * @memberof GameGuideManager
    * @protected
    * @instance
    * @type {Action}
    */
    onClick

   /**
    * Initialize game guide manager
    * @param {object}      config_ - Game guide section config
    * @param {HTMLElement} page_   - Game guide page node
    * @param {Promise<undefined>} loaded_ Promise for when buttonManager exposed to window
    */
   constructor(config_, page_, loaded_) {

      super(config_, page_) //Call parent constructor

      this.onClick = new Action()

      //Create div for header and translate header text
      let header       = document.createElement('div')
      header.innerHTML = Translate.do(config_[0].title)
      header.classList.add('paytable-header')

      page_.appendChild(header) //Add game guide header

      //Create div for game guide content
      this._body    = document.createElement('div')
      this._body.id = 'game-guide-content'

      let device = framework.isTouch() ? 'mobile' : 'desktop' //Flag for what help file to load
       
      const generateHelpPath = (lang_) => {
          const path = `properties/ironDog/help/${device}/${gameParams.folderName}/help_${lang_}.json`
          return IS_NATIVE ?
            `./${path}` :
            `${gameParams.pathCDN}f1x2games/${path}`
      }

      //game-guide request
      Axios.get(generateHelpPath(gameParams.lang))
       
      // Initial GET request successful, try to load english if language is not restricted
      .catch(e_ => {

        if (gameParams.lang === 'en' || gameParams.languageRestricted) {
          return Promise.reject(e_)
        }
        return Axios.get(generateHelpPath('en'))

      })

      //GET request successful
      .then(r_ => {

        //Loop through game guide
        for (let i = 0; i < r_.data.length; i++) {

          let baf = r_.data[i].id //Store flag for buy a feature specific game guide sections

          //If section should be displayed depending if buy a feature is enabled/disabled
          if (baf === undefined || baf === '_BaF_' && buyFeatureEnabled_() || baf === '_noBaF_' && !buyFeatureEnabled_()) {

            //If game guide section has title
            if (r_.data[i].title) {

              let title = r_.data[i].title //Store title

              //Add title
              let p       = document.createElement('p')
              p.innerHTML = title
              this._body.appendChild(p)

            }

            let content = r_.data[i].body //Store content

            //Create unordered list
            let ul = document.createElement('ul')

            //If mobile
            if (framework.isTouch()) {
              ul.classList.add(ORIENTATION.orientation) //Apply orientation based styling
            }

            //Loop through content
            for(let j = 0; j < content.length; j++) {

              let li = document.createElement('li') //Create list item for sentence

              //If mobile
              if (framework.isTouch()) {
                li.classList.add(ORIENTATION.orientation) //Apply orientation based styling
              }

              //If list contains indented list
              if (content[j].match(/\%indent(.*?)\%indent/g)) {

                //Style indented list inside list
                li.classList.add('indent-value')
                content[j] = content[j].replace(/%indent/g, '')

              }

              //Replace bold placeholder (if exists) within sentence with bold styled span
              content[j] = content[j].replace(/\%b(.*?)\%b/g, w_ => {

                w_ = w_.replace(/%b/g, '')
                return `<span class='bold-value'>${w_}</span>`

              })

              let rtp = config_[0].rtp[gameParams.confType - 1] //Store RTP

              //If RTP not set
              if (!rtp) {
                rtp = 'RTP NOT FOUND' //Display error
              }

              //Replace RTP placeholder within sentence with rtp styled span
              content[j] = content[j].replace(/%rtp/, `<span class='rtp'>${rtp}</span>`)

              li.innerHTML = content[j] //Add sentence contents
              ul.appendChild(li)        //Add list item to list

            }

            this._body.appendChild(ul) //Add list to game guide

          }

        }

        this.buttonsDescription()                                               //Generate buttons description
        loaded_.then(() => setTimeout(() => window.newUI.helpResolved(), 500))  //Resolve helpLoaded Promise

      })

      //GET request unsuccessful
      .catch(e_ => {

        console.error(e_)                                                       //Log error
        this._body.innerHTML = 'Game Guide Unavailable'                         //Show error message
        loaded_.then(() => setTimeout(() => window.newUI.helpResolved(), 500))  //Resolve helpLoaded Promise

      })

      page_.appendChild(this._body) //Add game guide to game guide page

   }

   /** Generates button description in game guide */
   buttonsDescription() {

    //Create buttons description title
    let title   = Translate.do('GUI Descriptions')
    let p       = document.createElement('p')
    p.innerHTML = title

    this._body.appendChild(p) //Add to page

    let ul = document.createElement('ul')   //Create unordered list to store buttons
    ul.classList.add('buttons-list')        //Add styling

    let config = { ...BUTTON_CONFIG['console'], ...MENU_BUTTON_CONFIG } //Spread configs into 1 config object

    //Loop through buttons
    for (let b in config) {

      let texts = config[b].text //Retrieve button description texts

      //If button has button description texts
      if (texts.length > 0) {

        //Loop through button description texts
        for (let i = 0; i < texts.length; i++) {

          //If text exists for a corresponding state
          if (texts[i] !== '') {

            //Create list item
            let li = document.createElement('li')
            li.classList.add('buttons-description')
            framework.isTouch() && li.classList.add(ORIENTATION.orientation)

            //If button optional
            if (config[b].optional) {

              //Mutliple sections
              if (config[b].text && config[b].text.length > 1) {
                  li.id = `optional-${config[b].optional.id}-${i}` //Set li id
              }

              //Single section
              else {
                  li.id = `optional-${config[b].optional.id}` //Set li id
              }

              //When user clicks to load game guide
              this.onClick.add(() => {

                let { manager, func, negate, prop, condition, override = () => { return true } } = config[b].optional.check //Destructure check-config

                //If need to check IDS manager specific property
                if (prop) {

                  //If bespoke condition needs to be checked to show button
                  if (!!condition) {

                    //If bespoke condition is met
                    if (condition(window[manager][func]()[prop])) {
                      document.getElementById(`optional-${config[b].optional.id}`).classList.add('hidden') //Hide from buttons description
                    }

                  }

                  //If button not in game
                  else if (!window[manager][func]().prop) {
                    document.getElementById(`optional-${config[b].optional.id}`).classList.add('hidden') //Hide from buttons description
                  }

                }

                //Only need to check IDS manager specific function
                else {

                    //If manager class needs to be called
                    if (manager) {

                        //If button not in game
                        if (!window[manager][func]()) {
                            document.getElementById(`optional-${config[b].optional.id}`).classList.add('hidden') //Hide from buttons description
                        }

                        //Button in game
                        else {

                          //If override function is true
                          if (override()) {

                            let elem = document.getElementById(`optional-${config[b].optional.id}`)
                            elem && elem.classList.add('hidden')
                          }
                        }

                    }

                    //Check function by calling directly on window object
                    else {

                        //If value should be negated
                        if (negate) {

                            //If button not in game
                            if (!window[func]()) {

                                //If multiple sections needs hiding
                                if (config[b].text && config[b].text.length > 1) {
                                    document.getElementById(`optional-${config[b].optional.id}-${i}`).classList.add('hidden')
                                }

                                //Single section needs hiding
                                else {
                                    document.getElementById(`optional-${config[b].optional.id}`).classList.add('hidden')
                                }

                            }

                        }

                        //Value not to be negated
                        else {

                            if (window[func]()) {
                                //TODO (not need atm)
                            }

                        }

                    }

                }

              })

            }

            //Create span to store button description text
            let span       = document.createElement('span')
            span.innerHTML = Translate.do(texts[i])
            li.appendChild(span)

            let s       = config[b]['states'] && config[b]['states'][i] //Store state
            let ignore  = config[b]['ignore']                           //Store ignore config
            let toAdd   = true                                          //Flag whether to add button to button description

            let asset = config[b].img || window.newUI.assets[b][s].up  //Store asset
            let div   = document.createElement('div')                  //Create div to contain asset

            //Check if mobile and different mobile image
            if (framework.isTouch() && config[b].mobImg ) {
              asset = config[b].mobImg  //Reset asset to mobile img
              div.style.opacity = 0.5   //Lower opacity since mobImgs active by default
            }

            //If button is menu or paytable and state is close
            if ((b === 'menu' || b === 'paytable') && s === 'CLOSE') {

              asset = ICONS.exit.desktop      //Display close icon
              div.style.opacity = 0.5 //Style icon

            }

            //If button is menu or paytable and state is close
            if ((b === 'menu' || b === 'paytable') && s === 'CLOSE') {

              asset = ICONS.exit.desktop      //Display close icon
              div.style.opacity = 0.5 //Style icon

            }

            //If single state to be ignored always
            if (typeof ignore === 'string') {

              //If state to be ignored
              if (ignore === s) {
                toAdd = false //Dont add to DOM
              }

            }

            //All state to be ignored
            else if (typeof ignore === 'boolean') {

              //If states to be ignored
              if (ignore) {
                toAdd = false //Dont add to DOM
              }

            }

            //If config to determine states to ignore
            else if (typeof ignore === 'object') {

              //If config exists for given state
              if (config[b].ignore[s]) {

                let { func, negate } = config[b].ignore[s] //Destructure ignore-config

                //If function to determine ignore
                if (func) {

                  //If result to be negated
                  if (negate) {
                    toAdd = !window[func]() ? true : false  //Determine ignore
                  }

                  //Take result as is
                  else {
                    toAdd = window[func]() ? true : false //Determine ignore
                  }
                }
              }

            }

            //Add asset to div
            asset.match(/<svg\s/g) ? div.appendChild(this.svg(asset)) : this.image(asset, div, config[b].custom)

            li.appendChild(div)         //Add div to list
            toAdd && ul.appendChild(li) //Add list item

          }

        }

      }

    }

    this._body.appendChild(ul) //Add list to game guide

   }

   /**
    * Loads a Data URI into a div
    * @param {string} asset_       - Data URI of asset
    * @param {HTMLDivElement} div_ - Div to add asset to
    * @param {boolean} custom_     - Whether custom size styling needed
    */
   image(asset_, div_, custom_) {

    let img = new Image() //Create new image

    //Once image loaded
    img.onload = (() => {

      div_.style.backgroundImage = `url(${asset_})` //Set div background image of asset

      //If custom size styling needed
      if (custom_) {

        //Set custom size styling
        div_.style.height  = `${img.height}px`
        div_.style.width   = `${img.width}px`

      }

    })

    img.src = asset_ //Set data URI as image source

   }

   /**
    * Returns an SVG HTML from a string representing an SVG asset
    * @param {string} asset_ - String containing SVG representing asset
    * @return {Element} SVG as an HTML element
    */
   svg(asset_) {

    let elem = new DOMParser().parseFromString(asset_, 'text/xml') //Parse SVG string
    return elem.documentElement                                    //Return SVG as an HTML element

   }

 }